import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaChartLine,
  FaCreditCard,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaWifi,
  FaTicketAlt,
  FaEnvelope,
  FaPhone,
  FaCalendar,
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaDownload,
  FaFilter,
  FaRedo,
  FaFileInvoice
} from 'react-icons/fa';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { API_ENDPOINTS } from '../../config/api';
import { adminService } from '../services/adminService';
import DataQualityBanner from './DataQualityBanner';

const AdminDashboard = ({ stats, onNavigate }) => {
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [planStats, setPlanStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Use the service to fetch stats securely
      const data = await adminService.getDashboardStats();

      const { stats: apiStats, recentCustomers: apiRecent } = data;

      if (apiRecent && apiRecent.length > 0) {
        setRecentCustomers(
          apiRecent.map((customer, index) => ({
            id: customer.UID,
            name: customer.fullName || `${customer.firstName} ${customer.lastName}`,
            email: customer.email,
            plan: customer.plan,
            status: customer.status || 'pending',
            date: new Date(customer.createdAt).toLocaleDateString(),
            accountNumber: customer.accountNumber,
            key: customer.UID || `customer-${index}`
          }))
        );
      }

      if (apiStats) {
        if (apiStats.planDistribution) {
          setPlanStats(apiStats.planDistribution);
        }
        if (stats && typeof stats === 'object') {
          Object.assign(stats, apiStats);
        }
      }

      // Fetch Mock tickets - in future use adminService.getTickets()
      const mockRecentTickets = [
        {
          id: 'TKT-001',
          customer: 'John Smith',
          subject: 'Connection issues',
          priority: 'high',
          status: 'open',
          date: '2025-08-07'
        },
        {
          id: 'TKT-002',
          customer: 'Sarah Johnson',
          subject: 'Billing inquiry',
          priority: 'medium',
          status: 'in_progress',
          date: '2025-08-06'
        }
      ];
      setRecentTickets(mockRecentTickets);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'on_hold': return 'text-orange-600 bg-orange-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Render a month-over-month growth badge. Returns "—" when prior data is
  // missing/zero so we never lie about growth like the old hardcoded "+12%".
  const GrowthBadge = ({ current, previous, suffix = '%', positiveIsGood = true }) => {
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) {
      return (
        <span className="text-sm text-[#928374] flex items-center mt-1">
          — no prior-month data
        </span>
      );
    }
    const delta = ((c - p) / p) * 100;
    const up = delta >= 0;
    const good = positiveIsGood ? up : !up;
    const color = good ? 'text-[#b8bb26]' : 'text-[#fb4934]';
    const Icon = up ? FaArrowUp : FaArrowDown;
    return (
      <span className={`text-sm ${color} flex items-center mt-1`}>
        <Icon size={12} className="mr-1" />
        {up ? '+' : ''}{delta.toFixed(1)}{suffix} from last month
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fabd2f]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2]">Dashboard</h1>
          <p className="text-[#bdae93]">Welcome back! Here's what's happening with Peaknet today.</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="flex items-center space-x-2 px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors"
        >
          <FaRedo size={16} />
          <span>Refresh</span>
        </button>
      </div>

      <DataQualityBanner dataQuality={stats.dataQuality} />

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Customers */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Total Customers</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">{stats.totalCustomers}</p>
              <GrowthBadge current={stats.totalCustomers} previous={stats.lastMonthCustomers} />
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaUsers className="text-[#fabd2f]" size={24} />
            </div>
          </div>
        </div>

        {/* Monthly Revenue */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Monthly Revenue</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">${(stats.monthlyRevenue || 0).toLocaleString()}</p>
              <GrowthBadge current={stats.monthlyRevenue} previous={stats.lastMonthRevenue} />
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaDollarSign className="text-[#b8bb26]" size={24} />
            </div>
          </div>
        </div>

        {/* DD Success Rate */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">DD Success Rate</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">
                {stats.ddSuccessRate == null ? '—' : `${stats.ddSuccessRate.toFixed(1)}%`}
              </p>
              {stats.ddSuccessRate == null ? (
                <p className="text-sm text-[#928374] flex items-center mt-1">no invoice data</p>
              ) : (
                <p className="text-sm text-[#b8bb26] flex items-center mt-1">
                  <FaCheckCircle size={12} className="mr-1" />
                  {stats.ddSuccess} successful
                </p>
              )}
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaCreditCard className="text-[#b8bb26]" size={24} />
            </div>
          </div>
        </div>

        {/* Active Tickets */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Active Tickets</p>
              <p className="text-3xl font-bold text-[#ebdbb2]">{stats.activeTickets}</p>
              <p className="text-sm text-[#fe8019] flex items-center mt-1">
                <FaClock size={12} className="mr-1" />
                {stats.pendingVerifications} pending
              </p>
            </div>
            <div className="w-12 h-12 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaTicketAlt className="text-[#fe8019]" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution Chart */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Plan Distribution</h3>
            <FaFilter className="text-[#7c6f64]" size={16} />
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(planStats).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {Object.entries(planStats).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#fabd2f', '#fe8019', '#b8bb26', '#8ec07c', '#d3869b'][index % 5]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1d2021', borderColor: '#3c3836', color: '#ebdbb2' }}
                  itemStyle={{ color: '#ebdbb2' }}
                />
                <Legend layout="vertical" align="right" verticalAlign="middle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* DD Status Breakdown */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Direct Debit Status</h3>
            <FaCreditCard className="text-[#928374]" size={16} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Successful', value: stats.ddSuccess || 0 },
                      { name: 'Rejected', value: stats.ddRejected || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    <Cell fill="#b8bb26" />
                    <Cell fill="#fb4934" />
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1d2021', borderColor: '#3c3836', color: '#ebdbb2' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-[#3c3836] rounded-lg border border-[#458588]">
                <div className="flex items-center space-x-3">
                  <FaCheckCircle className="text-[#b8bb26]" size={16} />
                  <span className="font-medium text-[#b8bb26]">Successful</span>
                </div>
                <span className="text-lg font-bold text-[#b8bb26]">{stats.ddSuccess}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#3c3836] rounded-lg border border-[#cc241d]">
                <div className="flex items-center space-x-3">
                  <FaExclamationTriangle className="text-[#fb4934]" size={16} />
                  <span className="font-medium text-[#fb4934]">Rejected</span>
                </div>
                <span className="text-lg font-bold text-[#fb4934]">{stats.ddRejected}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#3c3836] rounded-lg border border-[#83a598]">
                <div className="flex items-center space-x-3">
                  <FaClock className="text-[#83a598]" size={16} />
                  <span className="font-medium text-[#83a598]">Success Rate</span>
                </div>
                <span className="text-lg font-bold text-[#83a598]">
                  {stats.ddSuccess + stats.ddRejected > 0
                    ? Math.round((stats.ddSuccess / (stats.ddSuccess + stats.ddRejected)) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Customers */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Recent Customers</h3>
            <button className="text-[#fabd2f] hover:text-[#fe8019] text-sm font-medium">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentCustomers.map((customer, index) => (
              <div
                key={`recent-customer-${index}-${customer.id || customer.UID || customer.accountNumber || 'unknown'}`}
                className="flex items-center justify-between p-3 border border-[#3c3836] rounded-lg hover:bg-[#3c3836]"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                    {customer.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[#ebdbb2]">{customer.name}</p>
                    <p className="text-sm text-[#bdae93]">{customer.email}</p>
                    <p className="text-xs text-[#928374]">{customer.accountNumber}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(customer.status)}`}>
                    {customer.status.replace('_', ' ')}
                  </span>
                  <p className="text-xs text-[#928374] mt-1">{customer.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Tickets */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Recent Tickets</h3>
            <button className="text-[#fabd2f] hover:text-[#fe8019] text-sm font-medium">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentTickets.map((ticket, index) => (
              <div
                key={ticket.id || `ticket-${index}`}
                className="flex items-center justify-between p-3 border border-[#3c3836] rounded-lg hover:bg-[#3c3836]"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                    T
                  </div>
                  <div>
                    <p className="font-medium text-[#ebdbb2]">{ticket.subject}</p>
                    <p className="text-sm text-[#bdae93]">{ticket.customer}</p>
                    <p className="text-xs text-[#928374]">{ticket.id}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                  <p className="text-xs text-[#928374] mt-1">{ticket.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            key="view-customers"
            onClick={() => onNavigate && onNavigate('customers')}
            className="flex flex-col items-center p-4 border border-[#3c3836] rounded-lg hover:bg-[#3c3836] transition-colors"
          >
            <FaUsers className="text-[#fabd2f] mb-2" size={24} />
            <span className="text-sm font-medium text-[#ebdbb2]">View Customers</span>
          </button>
          <button
            key="manage-tickets"
            onClick={() => onNavigate && onNavigate('tickets')}
            className="flex flex-col items-center p-4 border border-[#3c3836] rounded-lg hover:bg-[#3c3836] transition-colors"
          >
            <FaTicketAlt className="text-[#fabd2f] mb-2" size={24} />
            <span className="text-sm font-medium text-[#ebdbb2]">Manage Tickets</span>
          </button>
          <button
            key="send-email"
            onClick={() => alert('Global email campaign feature coming in v2')}
            className="flex flex-col items-center p-4 border border-[#3c3836] rounded-lg hover:bg-[#3c3836] transition-colors"
          >
            <FaEnvelope className="text-[#fabd2f] mb-2" size={24} />
            <span className="text-sm font-medium text-[#ebdbb2]">Send Email</span>
          </button>
          <button
            key="manage-quotes"
            onClick={() => onNavigate && onNavigate('itservices-quotes')}
            className="flex flex-col items-center p-4 border border-[#3c3836] rounded-lg hover:bg-[#3c3836] transition-colors"
          >
            <FaFileInvoice className="text-[#fabd2f] mb-2" size={24} />
            <span className="text-sm font-medium text-[#ebdbb2]">IT Quotes</span>
          </button>
          <button
            key="view-invoices"
            onClick={() => onNavigate && onNavigate('itservices-invoices')}
            className="flex flex-col items-center p-4 border border-[#3c3836] rounded-lg hover:bg-[#3c3836] transition-colors"
          >
            <FaDollarSign className="text-[#fabd2f] mb-2" size={24} />
            <span className="text-sm font-medium text-[#ebdbb2]">IT Invoices</span>
          </button>
          <button
            key="export-data"
            onClick={() => alert('Exporting all data to CSV...')}
            className="flex flex-col items-center p-4 border border-[#3c3836] rounded-lg hover:bg-[#3c3836] transition-colors"
          >
            <FaDownload className="text-[#fabd2f] mb-2" size={24} />
            <span className="text-sm font-medium text-[#ebdbb2]">Export Data</span>
          </button>
        </div>
      </div>

      {/* Quick Stats (simple list, like original sidebar block) */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Quick Stats</h3>
        <div className="bg-gradient-to-r from-[#3c3836] to-[#504945] rounded-lg p-4 text-[#ebdbb2]">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Customers:</span>
              <span className="font-bold text-[#fabd2f]">{stats.totalCustomers}</span>
            </div>
            <div className="flex justify-between">
              <span>Monthly Revenue:</span>
              <span className="font-bold text-[#fabd2f]">
                ${(stats.monthlyRevenue || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Total Revenue:</span>
              <span className="font-bold text-[#fabd2f]">
                ${(stats.totalRevenue || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Active Services:</span>
              <span className="font-bold text-[#fabd2f]">
                {stats.activeServices || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span>DD Success Rate:</span>
              <span className="font-bold text-[#b8bb26]">
                {stats.ddSuccessRate ? `${stats.ddSuccessRate.toFixed(1)}%` : '0%'}
              </span>
            </div>
            <div className="flex justify-between">
              <span>DD Success:</span>
              <span className="font-bold text-[#b8bb26]">
                {stats.ddSuccess || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 